import { takeEvery, put } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import {
  getBusinessDataObj,
  getBusinessDataObjSuccess,
  getUserDataObj,
  getUserDataObjSuccess,
  updateUserLicence,
  getPermittedLicences,
  getBranchLicences,
  getMasterDataPicklist,
  userDataCallComplete,
  businessDataCallComplete,
} from "../../slices/BusinessSlice";
import { redirectToLicenseLandingPage } from "../../../helpers/commonHelpers";
import { getBranchData } from "../../slices/OutletStatusSlice";
import { METHODS } from "../../../data/constants";
import { getWindow } from "../../../effects-bridge";

import { fetchBranchLicencesFunc } from "./shared";

export * from "./shared";

/**
 * WEB variant of `redux-store/saga/business`. See `./shared.ts` for why no worker is shared.
 *
 * ⚠️ **A dead `import store from "../../index"` was dropped** (rule 2b / hazard 13). The app file
 * carried it and never referenced `store` in its body — measured, not assumed: the identifier
 * appeared exactly twice, both on import lines.
 *
 * ⚠️ `window.__businessDataFetched` is read through `getWindow()` INSIDE the worker, never at
 * module top level (hazard 18). It is a plain global flag other web code polls; core cannot touch
 * `window` directly because this package must stay RN-safe.
 *
 * ⚠️ Both `finally` blocks are load-bearing: the completion actions fire even when the request
 * throws, which is what lets the boot sequence stop waiting on a failed call. Preserved exactly.
 */

function* getBusinessData(data: any): any {
  try {
    const response = yield api.request({
      method: `GET`,
      url: ENDPOINT.COMMON.getBusinessData(),
      hideLoader: data?.payload?.hideLoader
    } as any);
    data.payload?.callback && data.payload.callback(response);
    if (!response?.code) {
      yield put(getBusinessDataObjSuccess(response));
      getWindow().__businessDataFetched = true;
      yield put(getBranchData(response?.branchId));
    }
  } catch (error) {
    console.log(error);
  } finally {
    yield put(businessDataCallComplete());
  }
}

function* fetchMasterDataPicklist(actions: any): any {
  try {
    const response = yield api.request({
      method: METHODS.GET,
      url: ENDPOINT.POS.getMasterDataPicklist(actions.payload.listType),
    });
    if (!response?.code && actions.payload.callback) {
      actions.payload.callback(response);
    }
  } catch (error) {
    console.log(error);
  }
}

function* getUserData(actions: any): any {
  try {
    const response = yield api.request({
      method: 'GET',
      url: ENDPOINT.COMMON.getUserData(),
      skipGlobalLoader: actions?.payload?.skipGlobalLoader
    } as any)
    if (response && !response?.code) {
      if (actions?.payload?.isRedirect)
        redirectToLicenseLandingPage(response?.assignedLicence);
      yield put(getUserDataObjSuccess(response));
      if (actions?.payload?.callback) {
        yield actions?.payload?.callback(response);
      }
    }
  } catch (error) {
    console.log(error)
  } finally {
    yield put(userDataCallComplete());
  }
}

function* updateExistingUserLicence(actions: any): any {
  try {
    const response = yield api.request({
      method: 'POST',
      url: ENDPOINT.COMMON.licenceUpdateApiUrl,
      data: actions.payload.data
    });
    yield put(getUserDataObj({ isRedirect: true }));
    yield actions.payload?.callback && actions.payload.callback(response);
  } catch (error) {
    console.log(error)
  }
}

function* fetachPermittedLicences(actions: any): any {
  try {
    const response = yield api.request({
      method: 'GET',
      url: ENDPOINT.COMMON.getAllPermittedLicenceApiUrl,
      skipGlobalLoader: actions?.payload?.skipGlobalLoader
    } as any);
    if (actions?.payload?.callback && response) {
      actions?.payload?.callback(response);
    }
  } catch (error) {
    console.log(error)
  }
}

export default function* root(): any {
  yield takeEvery(getBusinessDataObj.type, getBusinessData);
  yield takeEvery(getUserDataObj.type, getUserData);
  yield takeEvery(updateUserLicence.type, updateExistingUserLicence);
  yield takeEvery(getPermittedLicences.type, fetachPermittedLicences);
  yield takeEvery(getBranchLicences.type, fetchBranchLicencesFunc);
  yield takeEvery(getMasterDataPicklist.type, fetchMasterDataPicklist);
}
